import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Volume2, ChevronRight, ChevronLeft, Check, X, 
  MessageCircle, BookOpen, Trophy, Send, Mic, MicOff, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { selectMaleVoice, preloadVoices } from '../services/voiceService';

const API = process.env.REACT_APP_BACKEND_URL;

const LessonPage = () => {
  const navigate = useNavigate();
  const { pathId, moduleId, lessonId } = useParams();
  const { user } = useAuth();
  
  const [lesson, setLesson] = useState(null);
  const [pathName, setPathName] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Step management: vocabulary -> conversation -> quiz -> complete
  const [currentStep, setCurrentStep] = useState('vocabulary');
  const [vocabIndex, setVocabIndex] = useState(0);
  
  // Conversation state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Quiz state
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  
  // Speech
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchLesson();
    initSpeechRecognition();
    // Preload voices for TTS
    preloadVoices();
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [pathId, moduleId, lessonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = user?.target_language === 'English' ? 'en-US' : 
                                    user?.target_language === 'Spanish' ? 'es-ES' : 
                                    user?.target_language === 'French' ? 'fr-FR' : 
                                    user?.target_language === 'German' ? 'de-DE' : 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(prev => prev + (prev ? ' ' : '') + transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
  };

  const fetchLesson = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/learning/paths/${pathId}/modules/${moduleId}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Erro ao carregar lição');
      }
      
      const data = await res.json();
      setLesson(data.lesson);
      setPathName(data.path_name);
      setModuleName(data.module_name);
    } catch (error) {
      toast.error(error.message);
      navigate(`/learning/${pathId}`);
    } finally {
      setLoading(false);
    }
  };

  const speak = (text, lang = 'en-US') => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Use centralized voiceService for consistent male voice in lessons
      const targetLang = user?.target_language || 'English';
      const selectedVoice = selectMaleVoice(targetLang);
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
        console.log(`🎤 Lesson: Using male voice "${selectedVoice.name}"`);
      } else {
        utterance.lang = lang;
      }
      
      utterance.rate = 0.85;
      utterance.pitch = 0.85; // Lower pitch for male voice
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  // Start conversation with AI
  const startConversation = async () => {
    setCurrentStep('conversation');
    setIsTyping(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: `[LESSON_START] ${lesson.conversation_prompt}`,
          include_audio: false
        })
      });
      
      const data = await res.json();
      setConversationId(data.conversation_id);
      setMessages([{ role: 'assistant', content: data.response }]);
      speak(data.response);
    } catch (error) {
      toast.error('Erro ao iniciar conversa');
    } finally {
      setIsTyping(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isTyping) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: userMessage,
          include_audio: false
        })
      });
      
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      speak(data.response);
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setIsTyping(false);
    }
  };

  // Quiz functions
  const handleQuizAnswer = (answerIndex) => {
    if (showResult) return;
    
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    const currentQuiz = lesson.quiz[quizIndex];
    const isCorrect = answerIndex === currentQuiz.correct;
    
    setQuizAnswers(prev => [...prev, { question: quizIndex, correct: isCorrect }]);
    
    if (isCorrect) {
      setQuizScore(prev => prev + 1);
    }
  };

  const nextQuizQuestion = () => {
    if (quizIndex < lesson.quiz.length - 1) {
      setQuizIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      completeLesson();
    }
  };

  const completeLesson = async () => {
    setCurrentStep('complete');
    
    try {
      const token = localStorage.getItem('token');
      const finalScore = Math.round((quizScore / lesson.quiz.length) * 100);
      
      await fetch(`${API}/api/learning/progress/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          lesson_id: lessonId,
          quiz_score: finalScore
        })
      });
      
      toast.success('Lição concluída!');
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  if (loading || !lesson) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest text-lg">Carregando lição...</div>
      </div>
    );
  }

  const currentVocab = lesson.vocabulary[vocabIndex];
  const currentQuiz = lesson.quiz[quizIndex];

  return (
    <div className="min-h-screen bg-sand flex flex-col" data-testid="lesson-page">
      {/* Header */}
      <div className="bg-forest text-white p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/learning/${pathId}`)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white/70 truncate">{pathName} • {moduleName}</p>
            <h1 className="font-semibold truncate">{lesson.name}</h1>
          </div>
        </div>
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {['vocabulary', 'conversation', 'quiz'].map((step, idx) => (
            <div key={step} className="flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                  ${currentStep === step ? 'bg-lime text-forest' : 
                    (currentStep === 'complete' || 
                     (step === 'vocabulary' && currentStep !== 'vocabulary') ||
                     (step === 'conversation' && (currentStep === 'quiz' || currentStep === 'complete')))
                    ? 'bg-white/30 text-white' : 'bg-white/10 text-white/50'}`}
              >
                {step === 'vocabulary' ? <BookOpen className="w-4 h-4" /> :
                 step === 'conversation' ? <MessageCircle className="w-4 h-4" /> :
                 <Trophy className="w-4 h-4" />}
              </div>
              {idx < 2 && <div className="w-8 h-0.5 bg-white/20 mx-1" />}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* VOCABULARY STEP */}
        {currentStep === 'vocabulary' && (
          <div className="p-4 max-w-lg mx-auto">
            <h2 className="text-lg font-semibold text-forest mb-4 text-center">
              Vocabulário ({vocabIndex + 1}/{lesson.vocabulary.length})
            </h2>
            
            {/* Flashcard */}
            <div className="bg-white rounded-2xl shadow-lg p-6 min-h-[250px] flex flex-col items-center justify-center">
              <button
                onClick={() => speak(currentVocab.word)}
                className="mb-4 p-3 bg-forest/10 rounded-full hover:bg-forest/20 transition-colors"
                data-testid="vocab-audio-btn"
              >
                <Volume2 className="w-6 h-6 text-forest" />
              </button>
              
              <p className="text-2xl font-bold text-forest text-center">{currentVocab.word}</p>
              <p className="text-lg text-stone mt-2 text-center">{currentVocab.translation}</p>
              
              <div className="mt-4 p-3 bg-sand rounded-xl w-full">
                <p className="text-sm text-stone text-center italic">"{currentVocab.example}"</p>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setVocabIndex(prev => Math.max(0, prev - 1))}
                disabled={vocabIndex === 0}
                className="p-3 bg-white rounded-xl shadow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="vocab-prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-forest" />
              </button>
              
              {vocabIndex === lesson.vocabulary.length - 1 ? (
                <button
                  onClick={startConversation}
                  className="px-6 py-3 bg-forest text-white rounded-xl font-medium hover:bg-forest/90 transition-colors flex items-center gap-2"
                  data-testid="start-conversation-btn"
                >
                  Praticar Conversa
                  <MessageCircle className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setVocabIndex(prev => prev + 1)}
                  className="p-3 bg-forest text-white rounded-xl shadow hover:bg-forest/90 transition-colors"
                  data-testid="vocab-next-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mt-4">
              {lesson.vocabulary.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setVocabIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === vocabIndex ? 'bg-forest' : 'bg-forest/20'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* CONVERSATION STEP */}
        {currentStep === 'conversation' && (
          <div className="flex flex-col h-full">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-forest text-white rounded-br-sm'
                        : 'bg-white text-forest rounded-bl-sm shadow-sm'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    {msg.role === 'assistant' && (
                      <button
                        onClick={() => speak(msg.content)}
                        className="mt-2 text-xs text-forest/60 hover:text-forest flex items-center gap-1"
                      >
                        <Volume2 className="w-3 h-3" /> Ouvir
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-forest/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-forest/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-forest/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <div className="p-4 bg-white border-t border-forest/10">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setCurrentStep('quiz')}
                  className="text-sm text-forest underline"
                  data-testid="go-to-quiz-btn"
                >
                  Ir para o Quiz →
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleListening}
                  className={`p-3 rounded-xl transition-colors ${
                    isListening ? 'bg-red-500 text-white' : 'bg-sand text-forest hover:bg-forest/10'
                  }`}
                  data-testid="mic-btn"
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
                
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Digite sua mensagem..."
                  className="flex-1 p-3 bg-sand rounded-xl text-forest placeholder:text-stone/50 focus:outline-none focus:ring-2 focus:ring-forest/20"
                  data-testid="chat-input"
                />
                
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  className="p-3 bg-forest text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-forest/90 transition-colors"
                  data-testid="send-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QUIZ STEP */}
        {currentStep === 'quiz' && (
          <div className="p-4 max-w-lg mx-auto">
            <h2 className="text-lg font-semibold text-forest mb-2 text-center">
              Quiz ({quizIndex + 1}/{lesson.quiz.length})
            </h2>
            <p className="text-sm text-stone text-center mb-6">Teste seu conhecimento!</p>
            
            {/* Question */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <p className="text-lg font-medium text-forest text-center mb-6">
                {currentQuiz.question}
              </p>
              
              {/* Options */}
              <div className="space-y-3">
                {currentQuiz.options.map((option, idx) => {
                  const isSelected = selectedAnswer === idx;
                  const isCorrect = idx === currentQuiz.correct;
                  
                  let optionClass = 'bg-sand text-forest hover:bg-forest/10';
                  if (showResult) {
                    if (isCorrect) {
                      optionClass = 'bg-green-100 text-green-700 border-2 border-green-500';
                    } else if (isSelected && !isCorrect) {
                      optionClass = 'bg-red-100 text-red-700 border-2 border-red-500';
                    } else {
                      optionClass = 'bg-sand/50 text-stone';
                    }
                  } else if (isSelected) {
                    optionClass = 'bg-forest text-white';
                  }
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => handleQuizAnswer(idx)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl text-left transition-colors flex items-center gap-3 ${optionClass}`}
                      data-testid={`quiz-option-${idx}`}
                    >
                      <span className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-medium text-sm">
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="flex-1">{option}</span>
                      {showResult && isCorrect && <Check className="w-5 h-5" />}
                      {showResult && isSelected && !isCorrect && <X className="w-5 h-5" />}
                    </button>
                  );
                })}
              </div>
              
              {/* Next Button */}
              {showResult && (
                <button
                  onClick={nextQuizQuestion}
                  className="w-full mt-6 py-3 bg-forest text-white rounded-xl font-medium hover:bg-forest/90 transition-colors"
                  data-testid="quiz-next-btn"
                >
                  {quizIndex === lesson.quiz.length - 1 ? 'Ver Resultado' : 'Próxima Pergunta'}
                </button>
              )}
            </div>
            
            {/* Progress */}
            <div className="flex justify-center gap-1.5 mt-6">
              {lesson.quiz.map((_, idx) => {
                const answered = quizAnswers[idx];
                return (
                  <div
                    key={idx}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      answered?.correct ? 'bg-green-500' :
                      answered?.correct === false ? 'bg-red-500' :
                      idx === quizIndex ? 'bg-forest' : 'bg-forest/20'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* COMPLETE STEP */}
        {currentStep === 'complete' && (
          <div className="p-4 max-w-lg mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-20 h-20 bg-lime/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-forest" />
              </div>
              
              <h2 className="text-2xl font-bold text-forest mb-2">Lição Concluída!</h2>
              <p className="text-stone mb-6">{lesson.name}</p>
              
              {/* Score */}
              <div className="bg-sand rounded-xl p-4 mb-6">
                <p className="text-sm text-stone mb-1">Pontuação no Quiz</p>
                <p className="text-3xl font-bold text-forest">
                  {quizScore}/{lesson.quiz.length}
                </p>
                <p className="text-sm text-stone mt-1">
                  {Math.round((quizScore / lesson.quiz.length) * 100)}% de acertos
                </p>
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setCurrentStep('vocabulary');
                    setVocabIndex(0);
                    setQuizIndex(0);
                    setQuizScore(0);
                    setQuizAnswers([]);
                    setMessages([]);
                    setSelectedAnswer(null);
                    setShowResult(false);
                  }}
                  className="w-full py-3 bg-sand text-forest rounded-xl font-medium hover:bg-forest/10 transition-colors flex items-center justify-center gap-2"
                  data-testid="retry-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Refazer Lição
                </button>
                
                <button
                  onClick={() => navigate(`/learning/${pathId}`)}
                  className="w-full py-3 bg-forest text-white rounded-xl font-medium hover:bg-forest/90 transition-colors"
                  data-testid="back-to-module-btn"
                >
                  Voltar para o Módulo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonPage;
